import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Avatar } from 'primereact/avatar';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface Branch {
  branch_id: string;
  institute_id: string;
  name: string;
  license_number: string;
  license_start: string;
  license_expiry: string;
  address: string;
  pincode: number;
  total_pc: number;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  capacity: number;
  status: string;
  active_operator: {
    user_id: string;
    name: string;
    email: string;
    assigned_at: string;
  } | null;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  data: Branch[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface BranchesProps {
  onBranchClick?: (branchId: string) => void;
}

const Branches: React.FC<BranchesProps> = ({ onBranchClick }) => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .branch-card .p-card-body {
        padding: 0 !important;
      }
      .branch-card .p-card-content {
        padding: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [page, search]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });

      const response = await apiClient.get<ApiResponse>(`/v1/branches?${params}`);
      
      if (response.data.success) {
        setBranches(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load branches');
      }
    } catch (err: any) {
      console.error('Failed to fetch branches:', err);
      setError(err.response?.data?.error || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading && branches.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: '#f5f5f5',
        borderRadius: '12px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="3"
          />
          <p style={{
            marginTop: '16px',
            color: '#0f3d3e',
            fontSize: '0.95rem'
          }}>
            Loading branches...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '10px', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            margin: '0 0 4px 0',
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#0f3d3e'
          }}>
            Branches
          </h1>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '0.9rem'
          }}>
            {pagination.total} Total Branches
          </p>
        </div>

        <InputText
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search branches..."
          style={{
            width: '300px',
            height: '40px'
          }}
        />
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error}
          style={{ marginBottom: '20px' }}
        />
      )}

      {/* Branches Grid */}
      {branches.length === 0 && !loading ? (
        <div style={{
          padding: '60px',
          textAlign: 'center',
          background: 'white',
          borderRadius: '12px'
        }}>
          <p style={{ color: '#999', fontSize: '1rem' }}>
            No branches found
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(288px, 1fr))',
          gap: '16px'
        }}>
          {branches.map((branch) => {
            return (
              <Card
                key={branch.branch_id}
                className="branch-card"
                style={{
                  borderRadius: '20px',
                  border: 'none',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(15,61,62,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                {/* Card Header with Gradient */}
                <div style={{
                  background: 'linear-gradient(180deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
                  padding: '20px',
                  color: 'white',
                  borderRadius: '20px 20px 0 0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        margin: '0 0 6px 0',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: 'white'
                      }}>
                        {branch.name}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '0.8rem',
                        color: 'rgba(255,255,255,0.8)'
                      }}>
                        PIN: {branch.pincode}
                      </p>
                    </div>
                    <span style={{
                      padding: '5px 12px',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      background: branch.status === 'active' ? '#d4f17e' : '#ffcdd2',
                      color: branch.status === 'active' ? '#0f3d3e' : '#b71c1c'
                    }}>
                      {branch.status}
                    </span>
                  </div>

                  {/* Capacity Display */}
                  <div>
                    <p style={{
                      margin: '0 0 4px 0',
                      fontSize: '0.7rem',
                      color: 'rgba(255,255,255,0.7)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Capacity
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '1.6rem',
                      fontWeight: '700',
                      color: 'white'
                    }}>
                      {branch.capacity}
                    </p>
                  </div>
                </div>

                {/* Card Body */}
                <div style={{ padding: '20px' }}>
                  {/* License Info */}
                  <div style={{
                    marginBottom: '16px',
                    paddingBottom: '16px',
                    borderBottom: '1px dashed #e0e0e0'
                  }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '0.75rem',
                      color: '#999',
                      fontWeight: '600'
                    }}>
                      LICENSE
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '10px'
                    }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: '#0f3d3e',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <i className="pi pi-id-card" style={{ color: '#d4f17e', fontSize: '0.9rem' }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          margin: '0 0 2px 0',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: '#1a1a1a',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all'
                        }}>
                          {branch.license_number}
                        </p>
                        <p style={{
                          margin: 0,
                          fontSize: '0.7rem',
                          color: '#666'
                        }}>
                          Expires {formatDate(branch.license_expiry)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Active Operator */}
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{
                      margin: '0 0 10px 0',
                      fontSize: '0.75rem',
                      color: '#999',
                      fontWeight: '600'
                    }}>
                      ACTIVE OPERATOR
                    </p>
                    {branch.active_operator ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px',
                        background: '#f5f5f5',
                        borderRadius: '8px'
                      }}>
                        <Avatar
                          label={branch.active_operator.name.charAt(0)}
                          size="normal"
                          style={{
                            backgroundColor: '#0f3d3e',
                            color: '#d4f17e',
                            width: '36px',
                            height: '36px',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}
                          shape="circle"
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{
                            margin: '0 0 2px 0',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#1a1a1a'
                          }}>
                            {branch.active_operator.name}
                          </p>
                          <p style={{
                            margin: 0,
                            fontSize: '0.75rem',
                            color: '#666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {branch.active_operator.email}
                          </p>
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: '0.7rem',
                          color: '#999',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatDate(branch.active_operator.assigned_at)}
                        </p>
                      </div>
                    ) : (
                      <div style={{
                        padding: '12px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: '0.8rem',
                          color: '#999',
                          fontStyle: 'italic'
                        }}>
                          No operator assigned
                        </p>
                      </div>
                    )}
                  </div>

                  {/* View Details Button */}
                  <button 
                    onClick={() => onBranchClick && onBranchClick(branch.branch_id)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#0f3d3e',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#1a5555';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#0f3d3e';
                    }}
                  >
                    View Details
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginTop: '32px'
        }}>
          <Button
            icon="pi pi-chevron-left"
            outlined
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{
              color: '#0f3d3e',
              borderColor: '#0f3d3e'
            }}
          />
          <span style={{
            fontSize: '0.9rem',
            color: '#0f3d3e',
            fontWeight: '500'
          }}>
            Page {pagination.page} of {pagination.pages}
          </span>
          <Button
            icon="pi pi-chevron-right"
            outlined
            disabled={page === pagination.pages}
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            style={{
              color: '#0f3d3e',
              borderColor: '#0f3d3e'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Branches;